import { Directive, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { BaseContainer, ShUrlQueryService, UrlQueryState } from '@stylehaven/components';
import { isEqual } from 'lodash-es';
import { Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, startWith, take, takeUntil } from 'rxjs/operators';
import { DEFAULT_PAGE_SIZE } from '../helpers';
import { defaultPaginationInfo, PaginationInfo } from '../record/entities/pagination/pagination-info.entity';

@Directive()
export abstract class AbstractRecordOverviewContainerComponent extends BaseContainer implements OnDestroy, OnInit {
    userPaginationInfo$: ReplaySubject<PaginationInfo | undefined> = new ReplaySubject<PaginationInfo | undefined>(1);

    protected urlQueryService: ShUrlQueryService = inject(ShUrlQueryService);

    initialPageSize$!: Observable<number>;

    @Input() defaultPageSize: number = DEFAULT_PAGE_SIZE;

    ngOnInit(): void {
        this.loadPaginationStateFromUrl();
        this.initPageSize();

        this.initQueryChangeListener();
    }

    protected loadPaginationStateFromUrl(): void {
        this.urlQueryService
            .getUrlQueryState()
            .pipe(
                takeUntil(this.destroy$),
                take(1),
                map((state: UrlQueryState) => {
                    const paginationInfo: PaginationInfo = {
                        ...defaultPaginationInfo,
                        page: state.page || 0,
                        resultsPerPage: state.pageSize || this.defaultPageSize
                    };
                    return paginationInfo;
                }),
                distinctUntilChanged(isEqual)
            )
            .subscribe(paginationInfo => this.userPaginationInfo$.next(paginationInfo));
    }

    protected initPageSize(): void {
        this.initialPageSize$ = this.userPaginationInfo$.pipe(
            map(paginationInfo => paginationInfo?.resultsPerPage || this.defaultPageSize),
            filter(size => !!size),
            startWith(this.defaultPageSize),
            distinctUntilChanged(isEqual),
            takeUntil(this.destroy$)
        );
    }

    protected initQueryChangeListener() {
        this.urlQueryService
            .getStructuredQuery()
            .pipe(distinctUntilChanged(isEqual), skip(1), takeUntil(this.destroy$))
            .subscribe(query => {
                this.resetPaginationToFirstPage();
            });
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    paginationInfoChanged(paginationInfo: PaginationInfo): void {
        this.urlQueryService.setPaginationInfo({
            page: paginationInfo.page,
            resultsPerPage: paginationInfo.resultsPerPage
        });
        this.userPaginationInfo$.next(paginationInfo);
    }

    protected resetPaginationToFirstPage(): void {
        this.urlQueryService.setPage(0);
        this.userPaginationInfo$.pipe(take(1), takeUntil(this.destroy$)).subscribe(value => {
            this.userPaginationInfo$.next({
                ...(value || defaultPaginationInfo),
                page: 0
            });
        });
    }
}
